import { useMemo, useState } from "react";
import { CloudUpload, Copy, Eye, FilePlus2, Search, Trash2 } from "lucide-react";
import { useTenantOrganization, useWaTemplates } from "../../../lib/api";
import { PrimaryButton, Select } from "../primitives";
import { StatusPill, TemplateAvatar } from "./status";
import { focusRing } from "./ui";
import { relativeTime, typeLabel } from "./templateFormat";

const FILTERS = [
  ["all", "All"],
  ["pending", "Waiting"],
  ["approved", "Approved"],
  ["rejected", "Rejected"],
];

// name, category, language, status, updated, and the buttons
const COLUMNS = "grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_110px_170px_192px] md:grid-cols-[minmax(240px,1fr)_100px_130px_150px_100px_192px]";

const group = (row) => (row.status === "failed" ? "rejected" : row.status);

function Head() {
  const label = "text-[12px] font-semibold uppercase tracking-[0.05em] text-[#6B7280]";
  return (
    <div className={`hidden gap-4 border-b border-[#E5E7EB] bg-[#F9FAFB] px-5 py-3 sm:grid ${COLUMNS}`} aria-hidden="true">
      <span className={label}>Template</span>
      <span className={label}>Category</span>
      <span className={`${label} hidden md:block`}>Dealer</span>
      <span className={label}>Status</span>
      <span className={`${label} hidden md:block`}>Updated</span>
      <span />
    </div>
  );
}

// One template. The whole row opens it, and the eye says so. Every row can be duplicated, and a
// template Meta did not approve also gets a cloud button that loads it, ready to send again.
function Row({ row, dealerName, flash, onOpen, onResend, onDuplicate, onDelete }) {
  const again = row.status === "rejected" || row.status === "failed";
  return (
    <li className={`group relative list-none border-b border-[#F1F3F6] transition-colors duration-150 last:border-b-0 hover:bg-[#F6F8FB] ${flash ? "sa-row-flash" : ""}`}>
      <button
        type="button"
        onClick={() => onOpen(row)}
        aria-label={`View ${row.name}`}
        className={`absolute inset-0 h-full w-full cursor-pointer ${focusRing} focus-visible:ring-inset`}
      />
      <div className={`pointer-events-none relative grid items-center gap-4 px-5 py-4 ${COLUMNS}`}>
        <span className="flex min-w-0 items-center gap-4">
          <TemplateAvatar name={row.name} category={row.category} />
          <span className="min-w-0 break-words text-[15px] font-semibold leading-5 text-[#111827]">
            {row.name.split("_").map((part, i) => (i ? [<span key={i}>_</span>, <wbr key={`w${i}`} />, part] : part))}
          </span>
        </span>
        <span className="hidden text-[14px] capitalize text-[#374151] sm:block">{String(row.category).toLowerCase()}{row.template_type && row.template_type !== "text" ? ` · ${typeLabel(row.template_type)}` : ""}</span>
        <span className={`hidden truncate text-[14px] md:block ${dealerName ? "text-[#374151]" : "text-[#9CA3AF]"}`}>{dealerName || "All dealers"}</span>
        <span className="hidden sm:block">
          <StatusPill status={row.status} />
        </span>
        <span className="hidden text-[14px] text-[#6B7280] md:block">{relativeTime(row.decided_at || row.checked_at || row.created_at)}</span>
        <span className="flex items-center justify-end gap-2">
          {again && (
            <button
              type="button"
              onClick={() => onResend(row)}
              title="Send for approval again"
              aria-label={`Send ${row.name} for approval again`}
              className={`pointer-events-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-[#EFF5FC] text-[#2667B5] transition-colors duration-150 hover:bg-[#2667B5] hover:text-white ${focusRing}`}
            >
              <CloudUpload size={18} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onDuplicate(row)}
            title="Duplicate"
            aria-label={`Duplicate ${row.name}`}
            className={`pointer-events-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-[#F3F4F6] text-[#4B5563] transition-colors duration-150 hover:bg-[#111827] hover:text-white ${focusRing}`}
          >
            <Copy size={17} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(row)}
            title="Delete"
            aria-label={`Delete ${row.name}`}
            className={`pointer-events-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-[#F3F4F6] text-[#4B5563] transition-colors duration-150 hover:bg-[#DC2626] hover:text-white ${focusRing}`}
          >
            <Trash2 size={17} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => onOpen(row)}
            title="View"
            aria-label={`Open ${row.name}`}
            className={`pointer-events-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-[#F3F4F6] text-[#4B5563] transition-colors duration-150 hover:bg-[#2667B5] hover:text-white ${focusRing}`}
          >
            <Eye size={18} aria-hidden="true" />
          </button>
        </span>
      </div>
    </li>
  );
}

function Skeleton() {
  return (
    <ul className="m-0 list-none overflow-hidden rounded-xl border border-[#EAECF0] bg-white p-0 shadow-[0_1px_2px_rgba(16,24,40,0.04)]" aria-busy="true" aria-label="Loading templates">
      {[0, 1, 2, 3].map((i) => (
        <li key={i} className="flex items-center gap-4 border-b border-[#F1F3F6] px-5 py-4 last:border-b-0">
          <span className="h-11 w-11 animate-pulse rounded-full bg-[#F3F4F6]" />
          <span className="h-4 w-56 max-w-full animate-pulse rounded bg-[#E5E7EB]" />
        </li>
      ))}
    </ul>
  );
}

// The templates made for this brand. `action` is the New template button, kept in the toolbar.
export default function TemplateLibrary({ tenantId, brandName, highlightId, onOpen, onResend, onDuplicate, onDelete, onCreate, action }) {
  const { data: rows, isLoading, isError } = useWaTemplates(tenantId);
  const { data: org } = useTenantOrganization(tenantId);
  const dealers = org?.dealers || [];
  const dealerName = (id) => dealers.find((d) => d.id === id)?.name || "";
  const [dealer, setDealer] = useState("all"); // all | brand | a dealer's id
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const counts = useMemo(() => {
    const c = { all: 0, pending: 0, approved: 0, rejected: 0 };
    (rows || []).forEach((r) => {
      c.all += 1;
      if (c[group(r)] !== undefined) c[group(r)] += 1;
    });
    return c;
  }, [rows]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const forDealer = (r) => dealer === "all" || (dealer === "brand" ? r.dealer_id == null : String(r.dealer_id) === dealer);
    return (rows || []).filter((r) => (filter === "all" || group(r) === filter) && forDealer(r) && (!q || r.name.toLowerCase().includes(q)));
  }, [rows, filter, query, dealer]);

  if (isLoading) return <Skeleton />;
  if (isError) {
    return <p role="alert" className="m-0 rounded-xl bg-[#FEF2F2] px-4 py-3 text-[14px] text-[#991B1B]">Could not load the templates. Reload the page to try again.</p>;
  }
  if (!rows?.length) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-3xl bg-[#F9FAFB] px-6 py-16 text-center ring-1 ring-[#E5E7EB]">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#EFF5FC] text-[#2667B5]">
          <FilePlus2 size={24} aria-hidden="true" />
        </span>
        <h3 className="m-0 text-[17px] font-semibold text-[#111827]">No templates made here yet</h3>
        <p className="m-0 max-w-[420px] text-[14.5px] leading-6 text-[#4B5563]">
          A template is a message WhatsApp must approve before {brandName} can send it. Make the first one, and it shows up here.
        </p>
        <PrimaryButton onClick={onCreate}>Make a template</PrimaryButton>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div role="group" aria-label="Filter by status" className="inline-flex rounded-xl bg-[#F3F4F6] p-1">
          {FILTERS.map(([key, label]) => {
            const on = filter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                aria-pressed={on}
                className={`inline-flex h-9 cursor-pointer items-center gap-[7px] rounded-lg px-4 text-[14px] font-medium transition-colors duration-150 ${focusRing} ${
                  on ? "bg-white text-[#111827] shadow-[0_1px_2px_rgba(16,24,40,0.14)]" : "text-[#6B7280] hover:text-[#111827]"
                }`}
              >
                {label}
                <span className={`min-w-[18px] text-center text-[12.5px] tabular-nums ${on ? "text-[#2667B5]" : "text-[#9CA3AF]"}`}>{counts[key]}</span>
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {dealers.length > 0 && (
            <Select aria-label="Filter by dealer" value={dealer} onChange={(e) => setDealer(e.target.value)} className="w-full sm:w-[190px]">
              <option value="all">All dealers</option>
              <option value="brand">Whole brand only</option>
              {dealers.map((d) => (
                <option key={d.id} value={String(d.id)}>
                  {d.name}
                </option>
              ))}
            </Select>
          )}
          <label className="relative block w-full sm:w-[230px]">
            <span className="sr-only">Search templates by name</span>
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search"
              className="h-10 w-full rounded-xl border-0 bg-[#F3F4F6] pl-10 pr-3 text-[14px] text-[#111827] placeholder:text-[#9CA3AF] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#2667B5]"
            />
          </label>
          {action}
        </div>
      </div>

      {shown.length ? (
        <div className="overflow-hidden rounded-xl border border-[#EAECF0] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
          <Head />
          <ul className="m-0 list-none p-0">
            {shown.map((row) => (
              <Row key={row.id} row={row} dealerName={dealerName(row.dealer_id)} flash={row.id === highlightId} onOpen={onOpen} onResend={onResend} onDuplicate={onDuplicate} onDelete={onDelete} />
            ))}
          </ul>
        </div>
      ) : (
        <p className="m-0 rounded-2xl bg-[#F9FAFB] px-6 py-12 text-center text-[14.5px] text-[#6B7280] ring-1 ring-[#E5E7EB]">No template matches.</p>
      )}
    </div>
  );
}
